import { NONRKPAY_INVOICE_STATUSES, V3_INVOICE_TYPE_ID } from "@constants";
import utils from "@utils";
import { getDisputeQueryData } from "@queries/NonRKPayInvoiceQueries";
import {
	canAddInternalNotes,
	canApplyPayments,
	canApproveNonRKPayInvoice,
	canMoveToPendingAgencyReview,
	canArchiveOrUnarchive,
	canChangeToPendingClientReview,
	canDownload,
	canEstimateBeConverted,
	canEstimateBeFinalized,
	canInvoiceBeDeleted,
	canInvoiceRecalculate,
	canInvoiceRemindClient,
	canPrint,
	canUpdateInvoice,
	isEstimateRefundable,
	isInputDisabled,
	isInvoiceRefundable,
	isVoidDisabled,
	showAttachments,
	showTimecardsNotes,
	isCreatedOrPendingAgencyReview,
	isInvoiceV3,
	canInvoiceBeCreatedFromEstimate,
	isEstimateRefundEnabled,
	isEstimateArchiveEnabled
} from "./ToolbarActionHelpers";

export const getPrimaryActions = ({
	nonRKPayInvoiceDetailHook,
	toggleInvoiceStatusHistoryModal,
	toggleDisputeHistoryModal,
	isTimeCardNotesModalEnabled,
	isSuperAdmin,
	clientV4InvoicingDisputes,
	areThereDisputesInHistory,
}) => {
	const {
		notes,
		attachments,
		toggleTimeCardNotesModal,
		toggleAttachmentsModal,
		confirmSavePrint,
		downloadInvoicePDF,
		timecardAttachmentsInvoiceV4Enabled,
		invoiceState: {
			invoiceId,
		},
	} = nonRKPayInvoiceDetailHook;
	const { lang } = global;
	const primaryActionClassName = "btn btn-link navbar-btn";

	return [
		{
			id: "dispute-history",
			className: primaryActionClassName,
			label: lang.t("Dispute History"),
			onClick: toggleDisputeHistoryModal,
			condition: clientV4InvoicingDisputes && !!invoiceId && areThereDisputesInHistory,
		},
		{
			id: "history",
			className: primaryActionClassName,
			label: "History",
			onClick: toggleInvoiceStatusHistoryModal,
			condition: !!invoiceId,
		},
		{
			id: "timecard-notes",
			className: primaryActionClassName,
			label: `Notes (${notes})`,
			iconClass: "rkf rkf-clipboard",
			onClick: toggleTimeCardNotesModal,
			condition: showTimecardsNotes(invoiceId, isTimeCardNotesModalEnabled),
		},
		{
			id: "attachments",
			className: primaryActionClassName,
			label: `Attachments (${attachments})`,
			iconClass: "rkf rkf-Paperclip",
			onClick: toggleAttachmentsModal,
			condition: showAttachments(invoiceId, timecardAttachmentsInvoiceV4Enabled)
		},
		{
			id: "print-invoice",
			className: primaryActionClassName,
			label: "Print",
			iconClass: "rkf rkf-printer",
			onClick: confirmSavePrint,
			condition: invoiceId,
			disabled: !canPrint(nonRKPayInvoiceDetailHook),
		},
		{
			id: "download-invoice",
			className: primaryActionClassName,
			label: "Download",
			iconClass: "glyphicon glyphicon-download-alt",
			onClick: downloadInvoicePDF,
			condition: canDownload(nonRKPayInvoiceDetailHook, isSuperAdmin),
		},
	];
};

export const getDropdownActions = ({
	nonRKPayInvoiceDetailHook,
	isSuperAdmin,
	lang,
	handleSend,
	sendingInvoice,
}) => {
	const {
		toggleActionsDropdown,
		confirmSaveAndApplyPayment,
		voidEstimate,
		deleteInvoice,
		toggleInternalNotesModal,
		savingInvoice,
		handleSubmit,
		approveNonRKPayInvoice,
		recalculateNonRKPayInvoice,
		changeNonRKPayInvoiceStatus,
		nonrkpayDetailPrepayEnhancementsEnabled,
		allowVoidNonrkpayInvoicesV2Enabled,
		moveNonRKPayInvoiceToPendingAgencyReview,
		revertInvoiceToPendingAgencyReviewEnabled,
		invoiceState: {
			invoiceId,
			invoiceVersion,
			statusId
		},
	} = nonRKPayInvoiceDetailHook;

	return {
		toggleDropdown: toggleActionsDropdown,
		actions: [
			{
				id: "update-invoice",
				label: lang.t("Update"),
				onClick: () => handleSubmit(),
				condition: !isSuperAdmin,
				disabled: !canUpdateInvoice(nonRKPayInvoiceDetailHook, isSuperAdmin) || savingInvoice,
				title: lang.t("Update an existing invoice"),
				dataTestId: "nonrkpayinvoicedetail__dropdown-actions-update",
			},
			{
				id: "send-invoice",
				label: lang.t("Send"),
				onClick: () => handleSend(),
				condition: nonrkpayDetailPrepayEnhancementsEnabled && canEstimateBeFinalized(nonRKPayInvoiceDetailHook, isSuperAdmin),
				loading: sendingInvoice,
				dataTestId: "nonrkpayinvoicedetail__dropdown-actions-send",
			},
			{
				id: "internal-notes",
				label: lang.t("Add Internal Note"),
				onClick: toggleInternalNotesModal,
				condition: isInvoiceV3(invoiceVersion),
				disabled: !canAddInternalNotes(nonRKPayInvoiceDetailHook),
				dataTestId: "nonrkpayinvoicedetail__dropdown-actions-internal-notes",
			},
			{
				id: "view-payment",
				label: lang.t("View Payments"),
				onClick: () => confirmSaveAndApplyPayment(invoiceId),
				condition: invoiceVersion === 3,
				disabled: !invoiceId,
				title: lang.t(
					invoiceId !== ""
						? "Apply or unapply payment(s) for an invoice or estimate"
						: "Invoice or estimate must be saved before applying payment",
				),
				dataTestId: "nonrkpayinvoicedetail__dropdown-actions-view-payment",
			},
			{
				id: "apply-payment",
				label: lang.t("Apply Payments"),
				onClick: () => confirmSaveAndApplyPayment(invoiceId),
				condition: canApplyPayments(invoiceVersion, isSuperAdmin),
				disabled: !invoiceId,
				title: lang.t(
					invoiceId
						? "Apply or unapply payment(s) for an invoice or estimate"
						: "Invoice or estimate must be saved before applying payment"
				),
				dataTestId: "nonrkpayinvoicedetail__dropdown-actions-apply-payment",
			},
			{
				id: "void-invoice",
				label: lang.t("Void"),
				onClick: voidEstimate,
				condition: (allowVoidNonrkpayInvoicesV2Enabled || isInvoiceV3(invoiceVersion)),
				disabled: isVoidDisabled(nonRKPayInvoiceDetailHook, isSuperAdmin),
				dataTestId: "nonrkpayinvoicedetail__dropdown-actions-void",
			},
			{
				id: "delete-invoice",
				label: lang.t("Delete"),
				onClick: deleteInvoice,
				condition: !nonrkpayDetailPrepayEnhancementsEnabled && canInvoiceBeDeleted(nonRKPayInvoiceDetailHook),
				dataTestId: "nonrkpayinvoicedetail__dropdown-actions-delete-invoice",
			},
			{
				id: "approve-invoice",
				label: lang.t("Approve Invoice"),
				onClick: () => approveNonRKPayInvoice(invoiceId),
				condition: canApproveNonRKPayInvoice(statusId, isSuperAdmin),
				dataTestId: "nonrkpayinvoicedetail__dropdown-actions-approve-invoice",
			},
			{
				id: "revert-to-pending-agency-review",
				label: lang.t("Move to pending submission"),
				onClick: () => moveNonRKPayInvoiceToPendingAgencyReview(invoiceId),
				condition: revertInvoiceToPendingAgencyReviewEnabled && canMoveToPendingAgencyReview(statusId, nonRKPayInvoiceDetailHook),
				dataTestId: "nonrkpayinvoicedetail__dropdown-actions-revert-to-pending-agency-review",
			},
			{
				id: "change-pending-client-review",
				label: lang.t("Change to Pending Client Review"),
				onClick: () =>
					changeNonRKPayInvoiceStatus(
						invoiceId,
						NONRKPAY_INVOICE_STATUSES.PENDING_CLIENT_REVIEW
					),
				condition: canChangeToPendingClientReview(statusId, isSuperAdmin),
				dataTestId: "nonrkpayinvoicedetail__dropdown-actions-change-pending-client-review",
			},
			{
				id: "recalculate-invoice",
				label: lang.t("Recalculate Invoice"),
				onClick: () => recalculateNonRKPayInvoice(invoiceId),
				condition: canInvoiceRecalculate(nonRKPayInvoiceDetailHook, isSuperAdmin),
				dataTestId: "nonrkpayinvoicedetail__dropdown-actions-recalculate-invoice",
			},
		],
	};
};

export const getMainActions = ({
	nonRKPayInvoiceDetailHook,
	lang,
	isSuperAdmin,
	setShowSendRemindersModal,
	handleSend,
	sendingInvoice,
	clientV4InvoicingDisputes = false,
}) => {

	const {
		newNonrkpayPrepayFlowEnabled,
		onClickFinalizeEstimate,
		onClickConvertEstimateToInvoice,
		savingInvoice,
		finalizingEstimate,
		verifyingEstimateInvoiceable,
		generatingInvoiceFromEstimate,
		refundingInvoice,
		toggleRefundModal,
		handleSubmit,
		setShowRefundNonRKPayInvoiceEstimateModal,
		handleArchiveNonRKPayInvoice,
		availableFunds,
		startGenerateInvoiceFromEstimateFlow,
		invoiceState: {
			invoiceId,
			archived,
			finalized,
			voided,
			isEstimate,
			totals,
			statusId,
			estimateFinalized,
			activeDisputeId,
			typeId
		}
	} = nonRKPayInvoiceDetailHook;

	const areInvoiceDisputeReasonsAmended = (() => {
		if (!clientV4InvoicingDisputes) return true;

		if (!activeDisputeId) return true;

		const { isDisputeCompleted } = utils.getNonRKPayDisputeAmendmentStatus(getDisputeQueryData(activeDisputeId));
		return isDisputeCompleted;
	})()

	const isMainActionDisabled = isInputDisabled(nonRKPayInvoiceDetailHook, isSuperAdmin);
	const canBeConverted = canEstimateBeConverted(nonRKPayInvoiceDetailHook, isSuperAdmin);
	const sendInvoiceDisabled = (() => {
		/**
		 * Officer invoices in Created status should not be sendable
		 */
		if (!isEstimate && typeId === V3_INVOICE_TYPE_ID.OFFICER && statusId === NONRKPAY_INVOICE_STATUSES.CREATED) {
			return true;
		}

		/**
		 * If an invoice is finalized and has a status of "Created", we enable the send button.
		 */
		if (finalized && statusId === NONRKPAY_INVOICE_STATUSES.CREATED) {
			return false;
		}
		/**
		 * If the invoice has unresolved disputes, we disable the send button.
		 */
		if (!areInvoiceDisputeReasonsAmended)
			return true;

		/**
		 * This condition is the one that determines if the send button
		 * is disabled or not. But the previous conditions have to be checked first.
		 * so it doesn't interfere with the logic.
		 */
		return isMainActionDisabled;
	})();

	const getAmount = () => {
		// For invoices or estimates voided we always refund applied payments
		if (voided) return totals.appliedPayments;

		if (isEstimate) return availableFunds;

		return Math.abs(totals.balance);
	};

	const mainActionsButtonClass = "btn primary-action-button navbar-btn btn-primary";

	const canSendEstimateOrInvoice = () => {
		if (isEstimate) {
			return invoiceId && !voided && !estimateFinalized && !canEstimateBeFinalized(nonRKPayInvoiceDetailHook, isSuperAdmin);
		}

		return invoiceId && isCreatedOrPendingAgencyReview(statusId) && !voided;
	};

	const canRefundEstimateOrInvoice = () => {
		if (isEstimate) return isEstimateRefundable(nonRKPayInvoiceDetailHook, isSuperAdmin);

		return isInvoiceRefundable(nonRKPayInvoiceDetailHook, isSuperAdmin);
	}

	return [
		{
			id: "void-invoice",
			className: "btn primary-action-button navbar-btn btn-danger",
			label: lang.t("Voided"),
			onClick: null,
			condition: invoiceId && voided && (isEstimate ? !isEstimateRefundable(nonRKPayInvoiceDetailHook, isSuperAdmin) : true),
			disabled: true,
			dataTestId: "nonrkpayinvoicedetail__main-actions-void",
		},
		{
			id: "finalize-estimate",
			className: mainActionsButtonClass,
			label: lang.t("Finalize"),
			onClick: () => onClickFinalizeEstimate(),
			condition: newNonrkpayPrepayFlowEnabled && canEstimateBeFinalized(nonRKPayInvoiceDetailHook, isSuperAdmin),
			disabled: isMainActionDisabled || finalizingEstimate,
			loading: finalizingEstimate || verifyingEstimateInvoiceable || generatingInvoiceFromEstimate,
			dataTestId: "nonrkpayinvoicedetail__main-actions-finalize-estimate",
		},
		{
			id: "convert-estimate",
			className: mainActionsButtonClass,
			label: lang.t("Convert to Invoice"),
			onClick: () => onClickConvertEstimateToInvoice(),
			condition: !newNonrkpayPrepayFlowEnabled && canBeConverted,
			disabled: isMainActionDisabled,
			loading: savingInvoice,
			dataTestId: "nonrkpayinvoicedetail__main-actions-convert-estimate",
		},
		{
			id: "remind-client",
			className: mainActionsButtonClass,
			label: lang.t("Remind Client"),
			onClick: () => setShowSendRemindersModal(true),
			condition: canInvoiceRemindClient(nonRKPayInvoiceDetailHook),
			dataTestId: "nonrkpayinvoicedetail__main-actions-remind-client",
		},
		{
			id: "save-invoice",
			className: mainActionsButtonClass,
			label: lang.t("Save"),
			onClick: () => handleSubmit(),
			condition: !invoiceId,
			disabled: isMainActionDisabled || savingInvoice,
			loading: savingInvoice,
			dataTestId: "nonrkpayinvoicedetail__main-actions-save",
		},
		{
			id: "refund-balance",
			className: mainActionsButtonClass,
			label: `${lang.t("Refund")} ${utils.currencyFormatter(getAmount())}`,
			onClick: () => (isEstimate && !voided) ? setShowRefundNonRKPayInvoiceEstimateModal(true) : toggleRefundModal(getAmount()),
			condition: canRefundEstimateOrInvoice(),
			disabled: isEstimate ? !isEstimateRefundEnabled(nonRKPayInvoiceDetailHook, isSuperAdmin) : false,
			loading: refundingInvoice,
			dataTestId: "nonrkpayinvoicedetail__main-actions-refund-balance",
		},
		{
			id: "archive",
			label: lang.t(archived ? "Unarchive" : "Archive"),
			className: mainActionsButtonClass,
			onClick: () => handleArchiveNonRKPayInvoice(invoiceId, archived),
			condition: canArchiveOrUnarchive(nonRKPayInvoiceDetailHook, isSuperAdmin),
			disabled: isEstimate ? !isEstimateArchiveEnabled(nonRKPayInvoiceDetailHook, isSuperAdmin) : false,
			dataTestId: "nonrkpayinvoicedetail__main-actions-archive",
		},
		{
			id: "send-invoice-btn",
			className: mainActionsButtonClass,
			label: lang.t("Send"),
			onClick: () => handleSend(),
			condition: canSendEstimateOrInvoice(),
			loading: sendingInvoice,
			disabled: sendInvoiceDisabled,
			dataTestId: "nonrkpayinvoicedetail__main-actions-send",
		},
		{
			id: "create-invoice-btn",
			className: mainActionsButtonClass,
			label: lang.t("Create Invoice"),
			onClick: () => startGenerateInvoiceFromEstimateFlow(),
			condition: newNonrkpayPrepayFlowEnabled && canInvoiceBeCreatedFromEstimate(nonRKPayInvoiceDetailHook, isSuperAdmin),
			loading: verifyingEstimateInvoiceable || generatingInvoiceFromEstimate,
			disabled: generatingInvoiceFromEstimate,
			dataTestId: "nonrkpayinvoicedetail__main-actions-create-invoice",
		},
	];
};
